import { exchangeMetafieldIdentifiers } from "@redotech/redo-return-core-common/exchange-metafield-display";
import { MetafieldType } from "@redotech/redo-return-core-common/return-flow/condition-enums";
import type { CustomerPortalAppSettings } from "../../settings";
import {
  getVariantPickerColorMetafieldIdentifiers,
  getVariantPickerColorMetafields,
  type ShopifyMetafieldLike,
} from "./variant-picker-color-metafield";

type MetafieldSettings = Pick<
  CustomerPortalAppSettings,
  "variantPickerColorHexMetafield" | "shopifyMetafieldsToDisplayOnExchangeItems"
>;
interface MetafieldIdentifier {
  namespace: string;
  key: string;
}

/** Metafield identifiers to request on Storefront product queries. */
export function getStorefrontMetafieldQueryOptions(
  settings: MetafieldSettings | undefined,
): {
  variantMetafieldIdentifiers: MetafieldIdentifier[];
  productMetafieldIdentifiers: MetafieldIdentifier[];
} {
  return {
    variantMetafieldIdentifiers: [
      ...(getVariantPickerColorMetafieldIdentifiers(
        settings?.variantPickerColorHexMetafield,
      ) ?? []),
      ...exchangeIdentifiers(settings, MetafieldType.VARIANT),
    ],
    productMetafieldIdentifiers: exchangeIdentifiers(
      settings,
      MetafieldType.PRODUCT,
    ),
  };
}

export function getStorefrontVariantMetafields(
  metafields: ShopifyMetafieldLike[] | null | undefined,
  settings: MetafieldSettings | undefined,
): Record<string, string | null> | undefined {
  const values = {
    ...getVariantPickerColorMetafields(
      metafields,
      settings?.variantPickerColorHexMetafield,
    ),
    ...metafieldValues(
      metafields,
      exchangeIdentifiers(settings, MetafieldType.VARIANT),
    ),
  };
  return Object.keys(values).length > 0 ? values : undefined;
}

export function getStorefrontProductMetafields(
  metafields: ShopifyMetafieldLike[] | null | undefined,
  settings: MetafieldSettings | undefined,
): Record<string, string | null> | undefined {
  const values = metafieldValues(
    metafields,
    exchangeIdentifiers(settings, MetafieldType.PRODUCT),
  );
  return Object.keys(values).length > 0 ? values : undefined;
}

function exchangeIdentifiers(
  settings: MetafieldSettings | undefined,
  metafieldType: MetafieldType,
): MetafieldIdentifier[] {
  return exchangeMetafieldIdentifiers(
    settings?.shopifyMetafieldsToDisplayOnExchangeItems ?? [],
    metafieldType,
  );
}

function metafieldValues(
  metafields: ShopifyMetafieldLike[] | null | undefined,
  identifiers: MetafieldIdentifier[],
): Record<string, string | null> {
  const values: Record<string, string | null> = {};
  for (const { namespace, key } of identifiers) {
    const metafield = metafields?.find(
      (item) => item?.namespace === namespace && item.key === key,
    );
    if (metafield) {
      values[`${namespace}.${key}`] = metafield.value ?? null;
    }
  }
  return values;
}
